import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, ExternalLink, AlertTriangle, Target, PauseCircle, Users, Gift, Download } from 'lucide-react';
import { useAuthStore } from '@shared/state';
import { getCustomerPortalUrl } from '@shared/services/subscription';
import { deleteAccount, exportUserData, updateExamDate } from '@shared/services/auth';
import type { RecalibrationPreference, UserProfile } from '@shared/types';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { RecalibrationSettings } from '../components/recalibration/RecalibrationSettings';
import { CogniVibeCard } from '../components/profile/CogniVibeCard';
import { FullWizard } from '../components/recalibration/FullWizard';
import { useBillingContext } from '../hooks/useBillingContext';

export function Profile() {
  const user = useAuthStore((s) => s.user);
  const [fullWizardOpen, setFullWizardOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { mode, teamName } = useBillingContext();

  if (!user) return <Navigate to="/login" replace />;

  const isPaid = user.subscription !== 'free';
  const preference =
    (user as UserProfile).recalibrationPreference ?? ('default' as RecalibrationPreference);

  async function handleExportData() {
    setExportLoading(true);
    setExportError(null);
    try {
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cogniprint-data-export-${data.meta?.generatedAt?.slice(0, 10) ?? "export"}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Could not generate your export. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }

  async function handleManageBilling() {
    if (!user) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { url } = await getCustomerPortalUrl(user.uid);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setPortalError('Billing portal unavailable. Please contact support.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to open billing portal';
      setPortalError(
        message.includes('not-found')
          ? 'No active subscription found.'
          : 'Unable to open billing portal. Please try again.',
      );
    } finally {
      setPortalLoading(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold mb-8">Account & Billing</h1>

        {/* Account */}
        <Card>
          <h2 className="font-heading text-lg font-semibold mb-3">Account</h2>
          <div className="space-y-2 text-sm font-body">
            <div className="flex justify-between">
              <span className="text-content-muted">Email</span>
              <span className="text-content">{user.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-muted">Name</span>
              <span className="text-content">{user.displayName ?? 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-muted">Plan</span>
              <span className="text-content capitalize">{user.subscription}</span>
            </div>
          </div>
        </Card>

        {mode === 'team_seat' ? (
          /* Seat members don't manage billing — the team admin pays for the seat. */
          <Card className="mt-4">
            <div className="flex items-start gap-3">
              <Users size={18} className="text-indigo-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-lg font-semibold mb-1">Provided by your team</h2>
                <p className="text-xs text-content-muted font-body">
                  Your CogniPrint access is included with{' '}
                  {teamName ? <span className="text-content font-medium">{teamName}</span> : 'your team'}.
                  Billing and your subscription are managed by your team admin — there's nothing for
                  you to pay or set up. If you leave the team, your account stays and reverts to the
                  Free plan.
                </p>
              </div>
            </div>
          </Card>
        ) : mode === 'granted' ? (
          /* Complimentary admin grant — no Lemon Squeezy subscription to manage. */
          <Card className="mt-4">
            <div className="flex items-start gap-3">
              <Gift size={18} className="text-gold-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-lg font-semibold mb-1">Complimentary access</h2>
                <p className="text-xs text-content-muted font-body">
                  Your <span className="text-content font-medium capitalize">{user.subscription}</span> plan
                  is active as a complimentary grant — there's no billing or payment method to manage.
                  For questions about your access, contact your CogniPrint admin.
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Billing */}
            <Card className="mt-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h2 className="font-heading text-lg font-semibold mb-1">Billing</h2>
                  <p className="text-xs text-content-muted font-body">
                    {isPaid
                      ? 'Manage your subscription, update payment method, or download invoices.'
                      : 'Upgrade to access billing, invoices, and payment method management.'}
                  </p>
                </div>
              </div>
              {isPaid ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={handleManageBilling}
                    loading={portalLoading}
                  >
                    <CreditCard size={14} /> Manage Billing <ExternalLink size={12} />
                  </Button>
                  {portalError && (
                    <p className="mt-3 text-xs text-error font-body">{portalError}</p>
                  )}
                </>
              ) : (
                <a href="/pricing">
                  <Button>Upgrade</Button>
                </a>
              )}
            </Card>

            {/* Pause vs cancel — UJM Stage 11 */}
            {isPaid && (
              <Card className="mt-4">
                <div className="flex items-start gap-3 mb-3">
                  <PauseCircle size={18} className="text-indigo-400 shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-heading text-lg font-semibold mb-1">Need a break?</h2>
                    <p className="text-xs text-content-muted font-body">
                      Pause your subscription instead of cancelling. Billing stops, your CogniPrint
                      profile and learning history stay intact, and you pick up where you left off
                      whenever you're ready. Use the customer portal to set a pause window.
                    </p>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={handleManageBilling}
                  loading={portalLoading}
                >
                  <PauseCircle size={14} /> Pause subscription <ExternalLink size={12} />
                </Button>
              </Card>
            )}
          </>
        )}

        <ExamDateCard user={user as UserProfile} />

        <CogniVibeCard />

        {isPaid && (
          <RecalibrationSettings
            currentPreference={preference}
            onStartWizard={() => setFullWizardOpen(true)}
          />
        )}

        {/* Your data — POPIA/GDPR export */}
        <Card className="mt-8" padding="lg">
          <div className="flex items-start gap-3 mb-3">
            <Download size={18} className="text-content-muted shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-base font-semibold mb-1">Your data</h2>
              <p className="text-xs text-content-muted font-body">
                Download a complete copy of your CogniPrint data — profile, content, learning paths,
                tutor conversations, adaptation history, and recalibration sessions — as a JSON file.
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={handleExportData} disabled={exportLoading}>
            {exportLoading ? 'Preparing…' : 'Download my data'}
          </Button>
          {exportError && <p className="text-xs text-error mt-2 font-body">{exportError}</p>}
        </Card>

        {/* Danger zone — delete account */}
        <Card className="mt-8" padding="lg">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={18} className="text-error shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-base font-semibold mb-1">Delete account</h2>
              <p className="text-xs text-content-muted font-body">
                Permanently remove your account and all associated data — CogniPrint profile,
                uploads, learning paths, tutor sessions, and recalibration history. This cannot be undone.
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => setDeleteOpen(true)}
          >
            Delete my account
          </Button>
        </Card>
      </motion.div>

      <FullWizard open={fullWizardOpen} onClose={() => setFullWizardOpen(false)} />

      <DeleteAccountModal
        open={deleteOpen}
        email={user.email ?? ''}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}

function ExamDateCard({ user }: { user: UserProfile }) {
  const [examDate, setExamDate] = useState<string>(user.examDate ?? '');
  const [examLabel, setExamLabel] = useState<string>(user.examLabel ?? '');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setSavedMessage(null);
    try {
      await updateExamDate(user.uid, examDate.trim() || null, examLabel.trim() || null);
      setSavedMessage('Saved');
      window.setTimeout(() => setSavedMessage(null), 2500);
    } catch (err) {
      setSavedMessage(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function clearExam() {
    setExamDate('');
    setExamLabel('');
    setSaving(true);
    try {
      await updateExamDate(user.uid, null, null);
    } finally {
      setSaving(false);
    }
  }

  const hasExam = !!user.examDate;

  return (
    <Card className="mt-4">
      <div className="flex items-start gap-3 mb-3">
        <Target size={18} className="text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-lg font-semibold mb-1">Exam date</h2>
          <p className="text-xs text-content-muted font-body">
            Set a target date to see a readiness signal on your dashboard and get T-7 / T-1 reminders.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="block text-xs font-body text-content-muted mb-1">Date</span>
          <input
            type="date"
            value={examDate}
            onChange={(e) => setExamDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-body text-content-muted mb-1">Label (optional)</span>
          <input
            type="text"
            value={examLabel}
            onChange={(e) => setExamLabel(e.target.value)}
            placeholder="e.g. SAICA APC"
            maxLength={40}
            className="w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-content text-sm font-body focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/40 transition-colors"
          />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={save} loading={saving} disabled={!examDate}>
          {hasExam ? 'Update' : 'Save exam date'}
        </Button>
        {hasExam && (
          <button
            type="button"
            onClick={clearExam}
            disabled={saving}
            className="text-xs text-content-muted hover:text-content font-body cursor-pointer transition-colors disabled:opacity-50"
          >
            Clear
          </button>
        )}
        {savedMessage && (
          <span className="text-xs text-success font-body">{savedMessage}</span>
        )}
      </div>
    </Card>
  );
}

interface DeleteAccountModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
}

function DeleteAccountModal({ open, email, onClose }: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const canConfirm = confirmText.trim().toLowerCase() === email.trim().toLowerCase() && !submitting;

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await deleteAccount();
      // After deletion, Firebase Auth signs out; onAuthChange will redirect.
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account deletion failed. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl bg-dark-surface border border-dark-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={22} className="text-error shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg font-bold mb-1">Delete your account?</h3>
            <p className="text-sm text-content-secondary font-body">
              This will permanently delete your profile, uploads, learning paths, and all CogniPrint data.
              Your subscription will be cancelled. This action cannot be undone.
            </p>
          </div>
        </div>

        <label className="block text-xs text-content-muted font-body mb-1">
          Type your email to confirm: <span className="text-content font-mono">{email}</span>
        </label>
        <input
          type="email"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={email}
          className="w-full px-3 py-2 rounded-lg bg-dark-subtle border border-dark-border text-sm text-content font-body focus:outline-none focus:border-error transition-colors mb-4"
          autoComplete="off"
        />

        {error && (
          <p className="text-sm text-error font-body mb-4">{error}</p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-content-muted hover:text-content font-body transition-colors cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            variant="secondary"
            onClick={handleDelete}
            disabled={!canConfirm}
            loading={submitting}
          >
            Delete account
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
